import React, { useState } from "react";
import { AppLayout } from "../shared/layout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPlans, getSubscriptions, createSubscription, deleteSubscription } from "../../../data";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, AlertTriangle, CreditCard } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import AuthStore from "../../../stores/AuthStore";

export function SubscriptionScreen() {
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Fetch all plans
  const { data: plansData, isLoading: isPlansLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: getPlans,
  });

  // Fetch user's subscriptions
  // Assuming getSubscriptions returns all subscriptions, we might need to filter for the current user
  // But typically a user endpoint would return *my* subscriptions.
  // Let's assume getSubscriptions returns *my* subscriptions for now based on the context of "app" screens.
  // If it returns ALL subscriptions (admin style), we need to filter by AuthStore.user.id
  const { data: subscriptionData, isLoading: isSubLoading } = useQuery({
    queryKey: ["my-subscription"],
    queryFn: getSubscriptions,
  });

  // Filter for active subscription
  const activeSubscription = subscriptionData?.data?.find(
    (sub) => sub.status === "ACTIVE" && sub.userId === AuthStore.user?.id
  );

  const subscribeMutation = useMutation({
    mutationFn: createSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries(["my-subscription"]);
      toast({
        title: "Success",
        description: "Subscription updated successfully",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update subscription",
        variant: "destructive",
      });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: deleteSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries(["my-subscription"]);
      toast({
        title: "Success",
        description: "Subscription cancelled successfully",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to cancel subscription",
        variant: "destructive",
      });
    },
  });

  const handleSubscribe = (planId) => {
    // In a real app, this would likely redirect to a payment page or open a Stripe checkout
    // For now, we'll just call the API
    subscribeMutation.mutate({ planId });
  };

  const handleCancel = () => {
    if (activeSubscription) {
      cancelMutation.mutate(activeSubscription.id);
    }
  };

  const isLoading = isPlansLoading || isSubLoading;

  return (
    <AppLayout
      title="Subscription"
      description="Manage your plan and billing details"
      loading={isLoading}
    >
      <div className="space-y-8">
        {/* Current Subscription Status */}
        <Card className="border-l-4 border-l-primary">
          <CardHeader>
            <CardTitle>Current Subscription</CardTitle>
            <CardDescription>
              Your current billing status and plan details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {activeSubscription ? (
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-primary">
                    {activeSubscription.plan?.name}
                  </h3>
                  <p className="text-gray-500">
                    {activeSubscription.plan?.description}
                  </p>
                  <div className="mt-2 flex items-center text-sm text-gray-600">
                    <CreditCard className="mr-2 h-4 w-4" />
                    <span>
                      Renews on {new Date(activeSubscription.endDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant="success" className="mb-2">
                    Active
                  </Badge>
                  <p className="font-bold text-2xl">
                    ${activeSubscription.plan?.price}
                    <span className="text-sm font-normal text-gray-500">
                      /{activeSubscription.plan?.interval}
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-gray-500 mb-4">You don't have an active subscription.</p>
                <Button onClick={() => document.getElementById('plans').scrollIntoView({ behavior: 'smooth' })}>
                  View Plans
                </Button>
              </div>
            )}
          </CardContent>
          {activeSubscription && (
            <CardFooter className="bg-gray-50 dark:bg-gray-800/50 border-t px-6 py-4">
              <div className="flex justify-end w-full">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="text-red-600 hover:bg-red-50 border-red-200">
                      Cancel Subscription
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                      <AlertDialogAction onClick={handleCancel} className="bg-red-600 hover:bg-red-700">
                        Yes, Cancel
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardFooter>
          )}
        </Card>

        {/* Available Plans */}
        <div id="plans">
          <h3 className="text-2xl font-bold mb-6">Available Plans</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {plansData?.data?.map((plan) => {
              const isCurrentPlan = activeSubscription?.planId === plan.id;
              return (
                <Card
                  key={plan.id}
                  className={`relative flex flex-col ${isCurrentPlan ? 'border-primary ring-1 ring-primary shadow-lg' : ''}`}
                >
                  {isCurrentPlan && (
                    <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
                      <Badge className="bg-primary text-white">Current Plan</Badge>
                    </div>
                  )}
                  <CardHeader>
                    <CardTitle>{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <div className="text-3xl font-bold mb-6">
                      ${plan.price}
                      <span className="text-sm font-normal text-gray-500">
                        /{plan.interval}
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {/* Placeholder features - ideally these come from the API */}
                      <li className="flex items-center text-sm">
                        <Check className="mr-2 h-4 w-4 text-green-500" />
                        Unlimited Access
                      </li>
                      <li className="flex items-center text-sm">
                        <Check className="mr-2 h-4 w-4 text-green-500" />
                        Priority Support
                      </li>
                    </ul>
                  </CardContent>
                  <CardFooter>
                    <Button
                      className="w-full"
                      variant={isCurrentPlan ? "outline" : "default"}
                      disabled={isCurrentPlan || subscribeMutation.isLoading}
                      onClick={() => handleSubscribe(plan.id)}
                    >
                      {isCurrentPlan ? "Current Plan" : "Subscribe"}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
